<template>
  <div class="service-page">
    <!-- 顶部操作栏 -->
    <div class="action-bar">
      <div class="action-group">
        <button class="action-btn btn-primary" @click="showTicketModal = true">
          <span class="btn-icon">+</span>
          <span>新建工单</span>
        </button>
        <button class="action-btn btn-secondary" @click="handleBatchAssign">
          <span class="btn-icon">⚡</span>
          <span>批量分配</span>
        </button>
      </div>
      <div class="filter-group">
        <div class="filter-item">
          <span class="filter-label">优先级</span>
          <select v-model="filterPriority" class="filter-select">
            <option value="">全部</option>
            <option value="urgent">紧急</option>
            <option value="high">高</option>
            <option value="medium">中</option>
            <option value="low">低</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">状态</span>
          <select v-model="filterStatus" class="filter-select">
            <option value="">全部状态</option>
            <option value="pending">待处理</option>
            <option value="processing">处理中</option>
            <option value="resolved">已解决</option>
            <option value="closed">已关闭</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">类型</span>
          <select v-model="filterType" class="filter-select">
            <option value="">全部类型</option>
            <option value="technical">技术问题</option>
            <option value="usage">使用咨询</option>
            <option value="complaint">投诉建议</option>
            <option value="billing">账单问题</option>
          </select>
        </div>
        <div class="search-box">
          <input v-model="searchKeyword" type="text" placeholder="搜索工单号/客户..." class="search-input" />
          <span class="search-icon">🔍</span>
        </div>
      </div>
    </div>

    <!-- 服务指标概览 -->
    <div class="metrics-overview">
      <div class="metric-card" v-for="(item, index) in serviceMetrics" :key="index">
        <div class="metric-main">
          <div class="metric-icon" :style="{ background: item.color + '15', color: item.color }">
            <span v-html="item.icon"></span>
          </div>
          <div class="metric-info">
            <div class="metric-value">{{ item.value }}</div>
            <div class="metric-label">{{ item.label }}</div>
            <div class="metric-target" v-if="item.target">
              目标: {{ item.target }}
              <span :class="item.status">{{ item.status === 'good' ? '✓ 达标' : '✗ 未达标' }}</span>
            </div>
          </div>
        </div>
        <div class="metric-chart" :ref="'metricChart' + index"></div>
      </div>
    </div>

    <!-- 主体内容区 -->
    <div class="main-content">
      <!-- 工单列表 -->
      <div class="ticket-section" :class="{ 'detail-open': selectedTicket }">
        <div class="section-header">
          <div class="header-left">
            <span class="section-title">🎫 工单列表</span>
            <span class="section-count">共 {{ filteredTickets.length }} 条</span>
          </div>
          <div class="header-tabs">
            <span v-for="tab in listTabs" :key="tab.key" :class="{ active: activeListTab === tab.key }"
              @click="activeListTab = tab.key">
              {{ tab.label }}
              <span class="tab-badge" v-if="tab.count">{{ tab.count }}</span>
            </span>
          </div>
        </div>
        <div class="ticket-list">
          <div class="ticket-item" v-for="ticket in filteredTickets" :key="ticket.id" :class="{
            active: selectedTicket && selectedTicket.id === ticket.id,
            urgent: ticket.priority === 'urgent'
          }" @click="selectTicket(ticket)">
            <div class="ticket-priority">
              <span class="priority-dot" :class="ticket.priority"></span>
            </div>
            <div class="ticket-main">
              <div class="ticket-top">
                <div class="ticket-id">#{{ ticket.id }}</div>
                <div class="ticket-type" :style="{
                  background: getTypeColor(ticket.type) + '20',
                  color: getTypeColor(ticket.type)
                }">{{ getTypeName(ticket.type) }}</div>
                <div class="ticket-status" :class="ticket.status">
                  {{ getStatusName(ticket.status) }}
                </div>
              </div>
              <div class="ticket-title">{{ ticket.title }}</div>
              <div class="ticket-customer">
                <span>👤 {{ ticket.customer }}</span>
                <span>📞 {{ ticket.phone }}</span>
              </div>
              <div class="ticket-bottom">
                <div class="ticket-assignee">
                  <span class="assignee-avatar">{{ ticket.assignee.charAt(0) }}</span>
                  <span>{{ ticket.assignee }}</span>
                </div>
                <div class="ticket-meta">
                  <span class="ticket-time">⏱ {{ ticket.createTime }}</span>
                  <span class="ticket-sla" :class="ticket.slaStatus">
                    SLA {{ ticket.slaStatus === 'normal' ? '正常' : '预警' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 工单详情面板 -->
      <div class="detail-panel" v-if="selectedTicket">
        <div class="detail-header">
          <div class="detail-title">
            <h3>#{{ selectedTicket.id }} {{ selectedTicket.title }}</h3>
            <div class="detail-tags">
              <span class="tag" :class="selectedTicket.priority">{{ getPriorityName(selectedTicket.priority) }}</span>
              <span class="tag" :class="selectedTicket.status">{{ getStatusName(selectedTicket.status) }}</span>
            </div>
          </div>
          <button class="detail-close" @click="selectedTicket = null">✕</button>
        </div>

        <div class="detail-body">
          <!-- 客户信息 -->
          <div class="detail-section">
            <div class="section-title">👤 客户信息</div>
            <div class="customer-info">
              <div class="info-row">
                <div class="info-item">
                  <span class="info-label">客户名称</span>
                  <span class="info-value">{{ selectedTicket.customer }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">联系电话</span>
                  <span class="info-value">{{ selectedTicket.phone }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">客户等级</span>
                  <span class="info-value" :class="selectedTicket.customerLevel">
                    {{ getLevelName(selectedTicket.customerLevel) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 工单信息 -->
          <div class="detail-section">
            <div class="section-title">📋 工单详情</div>
            <div class="ticket-detail-content">
              <div class="detail-desc">{{ selectedTicket.desc }}</div>
              <div class="detail-attachments" v-if="selectedTicket.attachments && selectedTicket.attachments.length">
                <div class="attachment-item" v-for="(att, idx) in selectedTicket.attachments" :key="idx">
                  <span class="att-icon">📎</span>
                  <span class="att-name">{{ att.name }}</span>
                  <span class="att-size">{{ att.size }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 处理记录 -->
          <div class="detail-section">
            <div class="section-title">📝 处理记录</div>
            <div class="timeline">
              <div class="timeline-item" v-for="(record, idx) in selectedTicket.records" :key="idx">
                <div class="timeline-dot" :class="{ latest: idx === 0, system: record.type === 'system' }"></div>
                <div class="timeline-content">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ record.action }}</span>
                    <span class="timeline-author" v-if="record.author">by {{ record.author }}</span>
                    <span class="timeline-time">{{ record.time }}</span>
                  </div>
                  <div class="timeline-desc" v-if="record.desc">{{ record.desc }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 快捷操作 -->
          <div class="detail-actions">
            <button class="action-btn btn-primary" @click="handleReply">
              <span>💬 回复客户</span>
            </button>
            <button class="action-btn btn-secondary" @click="handleTransfer">
              <span>↻ 转交他人</span>
            </button>
            <button class="action-btn btn-success" @click="handleResolve"
              v-if="selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed'">
              <span>✓ 标记解决</span>
            </button>
            <button class="action-btn btn-warn" @click="handleClose" v-if="selectedTicket.status === 'resolved'">
              <span>🔒 关闭工单</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新建工单弹窗 -->
    <div class="modal-overlay" v-if="showTicketModal" @click.self="showTicketModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>新建服务工单</h3>
          <button class="modal-close" @click="showTicketModal = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label>客户名称 <span class="required">*</span></label>
              <input type="text" v-model="newTicket.customer" placeholder="请输入客户名称" />
            </div>
            <div class="form-item">
              <label>联系电话</label>
              <input type="text" v-model="newTicket.phone" placeholder="请输入联系电话" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>工单类型</label>
              <select v-model="newTicket.type">
                <option value="technical">技术问题</option>
                <option value="usage">使用咨询</option>
                <option value="complaint">投诉建议</option>
                <option value="billing">账单问题</option>
              </select>
            </div>
            <div class="form-item">
              <label>优先级</label>
              <select v-model="newTicket.priority">
                <option value="urgent">紧急</option>
                <option value="high">高</option>
                <option value="medium">中</option>
                <option value="low">低</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>负责人</label>
              <select v-model="newTicket.assignee">
                <option v-for="p in serviceStaff" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
            <div class="form-item">
              <label>客户等级</label>
              <select v-model="newTicket.customerLevel">
                <option value="vip">VIP</option>
                <option value="gold">金牌</option>
                <option value="silver">银牌</option>
                <option value="normal">普通</option>
              </select>
            </div>
          </div>
          <div class="form-item full">
            <label>问题描述 <span class="required">*</span></label>
            <textarea v-model="newTicket.desc" rows="4" placeholder="请详细描述客户问题..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showTicketModal = false">取消</button>
          <button class="btn-primary" @click="submitNewTicket">确认创建</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrmService',
  data() {
    return {
      filterPriority: '',
      filterStatus: '',
      filterType: '',
      searchKeyword: '',
      activeListTab: 'all',
      selectedTicket: null,
      showTicketModal: false,
      serviceStaff: ['李明', '王芳', '张伟', '刘洋', '陈静', '赵强'],
      serviceMetrics: [
        {
          icon: '🎫',
          value: '128',
          label: '今日工单',
          target: '≤150',
          status: 'good',
          color: '#4CAF50',
          sparkline: [110, 125, 98, 135, 142, 128, 115, 130, 138, 125, 120, 128]
        },
        {
          icon: '⏱',
          value: '12.5min',
          label: '平均响应',
          target: '≤15min',
          status: 'good',
          color: '#66BB6A',
          sparkline: [18, 16, 14, 15, 13, 12, 11, 13, 14, 12, 13, 12.5]
        },
        {
          icon: '✓',
          value: '96.8%',
          label: '解决率',
          target: '≥95%',
          status: 'good',
          color: '#81C784',
          sparkline: [92, 93, 94, 95, 95, 96, 96, 97, 96, 97, 96, 96.8]
        },
        {
          icon: '⭐',
          value: '4.8',
          label: '满意度',
          target: '≥4.5',
          status: 'good',
          color: '#A5D6A7',
          sparkline: [4.2, 4.3, 4.4, 4.5, 4.5, 4.6, 4.7, 4.7, 4.8, 4.8, 4.8, 4.8]
        }
      ],
      listTabs: [
        { key: 'all', label: '全部', count: 0 },
        { key: 'pending', label: '待处理', count: 8 },
        { key: 'processing', label: '处理中', count: 15 },
        { key: 'urgent', label: '紧急', count: 3 },
        { key: 'resolved', label: '已解决', count: 0 }
      ],
      tickets: [
        {
          id: '20260625001',
          title: '系统登录异常，无法访问后台',
          type: 'technical',
          priority: 'urgent',
          status: 'processing',
          customer: '华夏科技有限公司',
          phone: '138****8888',
          customerLevel: 'vip',
          assignee: '李明',
          createTime: '10分钟前',
          slaStatus: 'warning',
          desc: '客户反馈从今天上午9点开始，所有员工都无法登录系统后台，提示"连接超时"。已尝试重启浏览器和清除缓存，问题依然存在。影响范围：全公司50+员工。',
          attachments: [
            { name: 'error_screenshot.png', size: '245KB' },
            { name: 'network_log.txt', size: '12KB' }
          ],
          records: [
            { action: '工单受理', time: '2026-06-25 14:20', author: '李明', desc: '已确认问题，正在排查服务器连接状态' },
            { action: '问题升级', time: '2026-06-25 14:15', author: '系统', desc: '自动升级至紧急优先级，SLA预警' },
            { action: '工单创建', time: '2026-06-25 14:10', author: '王芳', desc: '客户通过电话反馈登录问题' }
          ]
        },
        {
          id: '20260625002',
          title: '咨询API接口文档和调用限制',
          type: 'usage',
          priority: 'medium',
          status: 'pending',
          customer: '创新未来集团',
          phone: '139****6666',
          customerLevel: 'gold',
          assignee: '张伟',
          createTime: '30分钟前',
          slaStatus: 'normal',
          desc: '客户技术团队需要最新的API接口文档，想了解每日调用次数限制和速率限制策略。希望安排一次技术对接会议。',
          records: [
            { action: '工单创建', time: '2026-06-25 13:50', author: '系统', desc: '客户通过在线客服提交咨询' }
          ]
        },
        {
          id: '20260625003',
          title: '对账单金额有疑问，需要核对',
          type: 'billing',
          priority: 'high',
          status: 'processing',
          customer: '鼎盛实业股份',
          phone: '137****9999',
          customerLevel: 'vip',
          assignee: '刘洋',
          createTime: '1小时前',
          slaStatus: 'normal',
          desc: '客户反馈6月份账单金额比预期高出3200元，怀疑存在重复计费。需要财务部门协助核对明细，并提供详细的使用量报告。',
          records: [
            { action: '资料调取', time: '2026-06-25 13:30', author: '刘洋', desc: '已调取客户6月完整使用记录，正在核对' },
            { action: '工单创建', time: '2026-06-25 13:20', author: '陈静', desc: '客户对账单金额提出异议' }
          ]
        },
        {
          id: '20260625004',
          title: '产品功能建议：增加批量导出',
          type: 'complaint',
          priority: 'low',
          status: 'pending',
          customer: '蓝海智能科技',
          phone: '136****5555',
          customerLevel: 'silver',
          assignee: '王芳',
          createTime: '2小时前',
          slaStatus: 'normal',
          desc: '客户建议增加数据批量导出功能，目前只能单条导出效率太低。建议支持Excel和CSV格式，并允许自定义字段选择。',
          records: [
            { action: '工单创建', time: '2026-06-25 12:30', author: '系统', desc: '客户通过产品反馈入口提交建议' }
          ]
        },
        {
          id: '20260625005',
          title: '服务器连接不稳定，频繁掉线',
          type: 'technical',
          priority: 'high',
          status: 'resolved',
          customer: '鹏程万里物流',
          phone: '135****7777',
          customerLevel: 'gold',
          assignee: '赵强',
          createTime: '3小时前',
          slaStatus: 'normal',
          desc: '客户反馈近两天服务器连接不稳定，平均每2小时掉线一次。已排查为网络供应商线路问题，已切换至备用线路。',
          records: [
            { action: '问题解决', time: '2026-06-25 13:00', author: '赵强', desc: '已切换至备用线路，连接恢复正常' },
            { action: '原因排查', time: '2026-06-25 12:30', author: '赵强', desc: '确认为网络供应商主线路故障' },
            { action: '工单创建', time: '2026-06-25 11:30', author: '系统', desc: '客户提交技术问题反馈' }
          ]
        },
        {
          id: '20260625006',
          title: '账户权限调整申请',
          type: 'usage',
          priority: 'medium',
          status: 'pending',
          customer: '星辰大海传媒',
          phone: '133****3333',
          customerLevel: 'normal',
          assignee: '陈静',
          createTime: '4小时前',
          slaStatus: 'normal',
          desc: '新入职的3名员工需要开通系统访问权限，同时需要调整部门主管的审批权限范围。',
          records: [
            { action: '工单创建', time: '2026-06-25 10:30', author: '系统', desc: '客户通过邮件提交权限申请' }
          ]
        },
        {
          id: '20260625007',
          title: '紧急：支付接口返回异常',
          type: 'technical',
          priority: 'urgent',
          status: 'processing',
          customer: '金石建筑工程',
          phone: '131****1111',
          customerLevel: 'vip',
          assignee: '李明',
          createTime: '15分钟前',
          slaStatus: 'warning',
          desc: '客户反馈支付接口返回500错误，影响线上交易。需要立即排查支付网关状态。',
          records: [
            { action: '紧急排查', time: '2026-06-25 14:25', author: '李明', desc: '正在联系支付服务商确认网关状态' },
            { action: '工单创建', time: '2026-06-25 14:15', author: '系统', desc: '自动标记为紧急工单' }
          ]
        },
        {
          id: '20260625008',
          title: '合同续费优惠政策咨询',
          type: 'usage',
          priority: 'low',
          status: 'pending',
          customer: '云端数据服务',
          phone: '132****2222',
          customerLevel: 'silver',
          assignee: '张伟',
          createTime: '5小时前',
          slaStatus: 'normal',
          desc: '客户合同即将到期，想了解续费优惠政策和升级套餐的方案对比。',
          records: [
            { action: '工单创建', time: '2026-06-25 09:30', author: '系统', desc: '客户通过销售转交咨询' }
          ]
        }
      ],
      newTicket: {
        customer: '',
        phone: '',
        type: 'technical',
        priority: 'medium',
        assignee: '李明',
        customerLevel: 'normal',
        desc: ''
      },
      charts: {}
    }
  },
  computed: {
    filteredTickets() {
      let result = this.tickets
      if (this.filterPriority) {
        result = result.filter(t => t.priority === this.filterPriority)
      }
      if (this.filterStatus) {
        result = result.filter(t => t.status === this.filterStatus)
      }
      if (this.filterType) {
        result = result.filter(t => t.type === this.filterType)
      }
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase()
        result = result.filter(t =>
          t.id.toLowerCase().includes(kw) ||
          t.title.toLowerCase().includes(kw) ||
          t.customer.toLowerCase().includes(kw)
        )
      }
      if (this.activeListTab !== 'all') {
        if (this.activeListTab === 'urgent') {
          result = result.filter(t => t.priority === 'urgent')
        } else {
          result = result.filter(t => t.status === this.activeListTab)
        }
      }
      return result
    }
  },
  watch: {
    filteredTickets: {
      handler(val) {
        if (val.length > 0) {
          const stillExists = val.find(t => t.id === (this.selectedTicket && this.selectedTicket.id))
          if (!stillExists) {
            this.selectTicket(val[0])
          }
        } else {
          this.selectedTicket = null
        }
      },
      immediate: true
    },
    selectedTicket: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            // 详情面板不需要图表，这里可以扩展
          })
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initMetricCharts()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    getTypeColor(type) {
      const map = { technical: '#4CAF50', usage: '#66BB6A', complaint: '#EF5350', billing: '#FFB74D' }
      return map[type] || '#4CAF50'
    },
    getTypeName(type) {
      const map = { technical: '技术问题', usage: '使用咨询', complaint: '投诉建议', billing: '账单问题' }
      return map[type] || type
    },
    getStatusName(status) {
      const map = { pending: '待处理', processing: '处理中', resolved: '已解决', closed: '已关闭' }
      return map[status] || status
    },
    getPriorityName(priority) {
      const map = { urgent: '紧急', high: '高', medium: '中', low: '低' }
      return map[priority] || priority
    },
    getLevelName(level) {
      const map = { vip: 'VIP', gold: '金牌', silver: '银牌', normal: '普通' }
      return map[level] || level
    },
    selectTicket(ticket) {
      this.selectedTicket = ticket
    },
    initMetricCharts() {
      this.serviceMetrics.forEach((item, index) => {
        const dom = this.$refs['metricChart' + index]
        if (!dom || !dom[0]) return
        const chart = this.$echarts.init(dom[0])
        const isLine = index === 1 // 平均响应用折线图，其他用柱状图

        const option = {
          grid: { top: 5, right: 5, bottom: 5, left: 5 },
          xAxis: { type: 'category', show: false, data: Array.from({ length: 12 }, (_, i) => i) },
          yAxis: { type: 'value', show: false },
          series: [{
            type: isLine ? 'line' : 'bar',
            data: item.sparkline,
            smooth: true,
            symbol: 'none',
            lineStyle: isLine ? { width: 2, color: item.color } : undefined,
            barWidth: isLine ? undefined : 4,
            itemStyle: {
              color: isLine ? undefined : new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: item.color },
                { offset: 1, color: item.color + '20' }
              ]),
              borderRadius: isLine ? undefined : [2, 2, 0, 0]
            },
            areaStyle: isLine ? {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: item.color + '30' },
                { offset: 1, color: 'transparent' }
              ])
            } : undefined
          }]
        }

        chart.setOption(option)
        this.charts['metric' + index] = chart
      })
    },
    handleBatchAssign() {
      alert('批量分配功能开发中...')
    },
    handleReply() {
      alert('回复客户功能开发中...')
    },
    handleTransfer() {
      alert('转交他人功能开发中...')
    },
    handleResolve() {
      if (this.selectedTicket) {
        this.selectedTicket.status = 'resolved'
        this.selectedTicket.records.unshift({
          action: '标记解决',
          time: new Date().toLocaleString('zh-CN'),
          author: '当前用户',
          desc: '问题已解决，等待客户确认'
        })
      }
    },
    handleClose() {
      if (this.selectedTicket) {
        this.selectedTicket.status = 'closed'
        this.selectedTicket.records.unshift({
          action: '关闭工单',
          time: new Date().toLocaleString('zh-CN'),
          author: '当前用户',
          desc: '工单已关闭'
        })
      }
    },
    submitNewTicket() {
      if (!this.newTicket.customer || !this.newTicket.desc) {
        alert('请填写必填项')
        return
      }
      const ticket = {
        id: '20260625' + String(Math.floor(Math.random() * 900) + 100).padStart(3, '0'),
        ...this.newTicket,
        status: 'pending',
        createTime: '刚刚',
        slaStatus: 'normal',
        records: [
          { action: '工单创建', time: new Date().toLocaleString('zh-CN'), author: '当前用户', desc: '手动创建工单' }
        ]
      }
      this.tickets.unshift(ticket)
      this.showTicketModal = false
      this.resetNewTicket()
      this.$nextTick(() => {
        this.selectTicket(this.filteredTickets[0])
      })
    },
    resetNewTicket() {
      this.newTicket = {
        customer: '',
        phone: '',
        type: 'technical',
        priority: 'medium',
        assignee: '李明',
        customerLevel: 'normal',
        desc: ''
      }
    },
    handleResize() {
      Object.values(this.charts).forEach(chart => {
        chart && chart.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.service-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  background: #0a1f0f;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.2);
    border-radius: 3px;
  }
}

// ===== 操作栏 =====
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  .action-group {
    display: flex;
    gap: 10px;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  .btn-icon {
    font-size: 14px;
    font-weight: 600;
  }

  &.btn-primary {
    background: linear-gradient(135deg, #2E7D32, #1B5E20);
    color: #e8f5e9;
    box-shadow: 0 2px 10px rgba(46, 125, 50, 0.3);

    &:hover {
      background: linear-gradient(135deg, #388E3C, #2E7D32);
      box-shadow: 0 4px 16px rgba(46, 125, 50, 0.4);
      transform: translateY(-1px);
    }
  }

  &.btn-secondary {
    background: rgba(255, 255, 255, 0.05);
    color: #a5d6a7;
    border: 1px solid rgba(76, 175, 80, 0.2);

    &:hover {
      background: rgba(76, 175, 80, 0.1);
      border-color: rgba(76, 175, 80, 0.4);
    }
  }

  &.btn-success {
    background: rgba(76, 175, 80, 0.15);
    color: #81C784;
    border: 1px solid rgba(76, 175, 80, 0.3);

    &:hover {
      background: rgba(76, 175, 80, 0.25);
    }
  }

  &.btn-warn {
    background: rgba(255, 152, 0, 0.1);
    color: #FFB74D;
    border: 1px solid rgba(255, 152, 0, 0.2);

    &:hover {
      background: rgba(255, 152, 0, 0.2);
    }
  }
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 6px;

  .filter-label {
    font-size: 12px;
    color: #66BB6A;
  }

  .filter-select {
    padding: 6px 10px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 13px;
    outline: none;
    cursor: pointer;

    &:focus {
      border-color: #4CAF50;
    }

    option {
      background: #102A15;
    }
  }
}

.search-box {
  position: relative;

  .search-input {
    width: 200px;
    padding: 7px 32px 7px 12px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #e8f5e9;
    font-size: 13px;
    outline: none;

    &::placeholder {
      color: #4CAF50;
      opacity: 0.5;
    }

    &:focus {
      border-color: #4CAF50;
    }
  }

  .search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    opacity: 0.6;
  }
}

// ===== 服务指标概览 =====
.metrics-overview {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 1480px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(76, 175, 80, 0.3);
    transform: translateY(-2px);
  }

  .metric-main {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .metric-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .metric-info {
    flex: 1;
    min-width: 0;

    .metric-value {
      font-size: 22px;
      font-weight: 700;
      color: #e8f5e9;
      font-family: 'Courier New', monospace;
      white-space: nowrap;
    }

    .metric-label {
      font-size: 12px;
      color: #66BB6A;
      margin-top: 2px;
      opacity: 0.8;
    }

    .metric-target {
      margin-top: 6px;
      font-size: 11px;
      color: #a5d6a7;
      opacity: 0.7;

      span {
        margin-left: 6px;
        font-weight: 500;

        &.good {
          color: #4CAF50;
        }

        &.bad {
          color: #EF5350;
        }
      }
    }
  }

  .metric-chart {
    width: 70px;
    height: 44px;
    flex-shrink: 0;
  }
}

// ===== 主体内容区 =====
.main-content {
  display: flex;
  gap: 16px;
  min-height: 500px;
}

// ===== 工单列表 =====
.ticket-section {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: flex 0.3s ease;

  &.detail-open {
    flex: 0 0 45%;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    @media (max-width: 1480px) {
      flex-direction: column;
      align-items: flex-start;
    }

    @media (max-width: 1200px) {
      flex-direction: row;
      align-items: center;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;

      .section-title {
        font-size: 15px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .section-count {
        font-size: 12px;
        color: #66BB6A;
        opacity: 0.7;
      }
    }

    .header-tabs {
      display: flex;
      gap: 4px;

      span {
        padding: 4px 12px;
        border-radius: 6px;
        font-size: 12px;
        color: #66BB6A;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;

        &:hover {
          background: rgba(76, 175, 80, 0.08);
        }

        &.active {
          background: rgba(76, 175, 80, 0.15);
          color: #e8f5e9;
          font-weight: 500;
        }

        .tab-badge {
          padding: 1px 6px;
          background: rgba(239, 83, 80, 0.2);
          color: #EF5350;
          border-radius: 8px;
          font-size: 10px;
          font-weight: 600;
        }
      }
    }
  }

  .ticket-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

.ticket-item {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 6px;
  background: rgba(76, 175, 80, 0.03);
  border: 1px solid rgba(76, 175, 80, 0.08);
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.06);
    border-color: rgba(76, 175, 80, 0.15);
  }

  &.active {
    background: rgba(76, 175, 80, 0.1);
    border-color: rgba(76, 175, 80, 0.35);
  }

  &.urgent {
    border-left: 3px solid #EF5350;
  }

  .ticket-priority {
    flex-shrink: 0;
    padding-top: 2px;

    .priority-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: block;

      &.urgent {
        background: #EF5350;
        box-shadow: 0 0 8px rgba(239, 83, 80, 0.5);
        animation: pulse 2s infinite;
      }

      &.high {
        background: #FFB74D;
      }

      &.medium {
        background: #81C784;
      }

      &.low {
        background: #66BB6A;
      }
    }
  }

  .ticket-main {
    flex: 1;
    min-width: 0;

    .ticket-top {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;

      .ticket-id {
        font-size: 12px;
        font-weight: 600;
        color: #81C784;
        font-family: 'Courier New', monospace;
      }

      .ticket-type {
        padding: 2px 8px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 500;
      }

      .ticket-status {
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 500;
        margin-left: auto;

        &.pending {
          color: #FFB74D;
          background: rgba(255, 183, 77, 0.1);
        }

        &.processing {
          color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }

        &.resolved {
          color: #81C784;
          background: rgba(129, 199, 132, 0.1);
        }

        &.closed {
          color: #66BB6A;
          background: rgba(102, 187, 106, 0.1);
        }
      }
    }

    .ticket-title {
      font-size: 14px;
      font-weight: 600;
      color: #e8f5e9;
      margin-bottom: 6px;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .ticket-customer {
      display: flex;
      gap: 12px;
      font-size: 12px;
      color: #66BB6A;
      margin-bottom: 8px;
      opacity: 0.8;
    }

    .ticket-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .ticket-assignee {
        display: flex;
        align-items: center;
        gap: 6px;

        .assignee-avatar {
          width: 22px;
          height: 22px;
          background: linear-gradient(135deg, #4CAF50, #2E7D32);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          color: #fff;
          font-weight: 600;
        }

        span:last-child {
          font-size: 12px;
          color: #a5d6a7;
        }
      }

      .ticket-meta {
        display: flex;
        align-items: center;
        gap: 10px;

        .ticket-time {
          font-size: 11px;
          color: #66BB6A;
          opacity: 0.7;
          font-family: 'Courier New', monospace;
        }

        .ticket-sla {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;

          &.normal {
            color: #4CAF50;
            background: rgba(76, 175, 80, 0.1);
          }

          &.warning {
            color: #EF5350;
            background: rgba(239, 83, 80, 0.1);
            animation: pulse 2s infinite;
          }
        }
      }
    }
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

// ===== 详情面板 =====
.detail-panel {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .detail-title {
      flex: 1;
      min-width: 0;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #e8f5e9;
        margin-bottom: 8px;
        line-height: 1.4;
      }

      .detail-tags {
        display: flex;
        gap: 8px;

        .tag {
          padding: 3px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;

          &.urgent {
            color: #EF5350;
            background: rgba(239, 83, 80, 0.1);
          }

          &.high {
            color: #FFB74D;
            background: rgba(255, 183, 77, 0.1);
          }

          &.medium {
            color: #81C784;
            background: rgba(129, 199, 132, 0.1);
          }

          &.low {
            color: #66BB6A;
            background: rgba(102, 187, 106, 0.1);
          }

          &.pending {
            color: #FFB74D;
            background: rgba(255, 183, 77, 0.1);
          }

          &.processing {
            color: #4CAF50;
            background: rgba(76, 175, 80, 0.1);
          }

          &.resolved {
            color: #81C784;
            background: rgba(129, 199, 132, 0.1);
          }

          &.closed {
            color: #66BB6A;
            background: rgba(102, 187, 106, 0.1);
          }
        }
      }
    }

    .detail-close {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #a5d6a7;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(239, 83, 80, 0.15);
        border-color: rgba(239, 83, 80, 0.3);
        color: #EF5350;
      }
    }
  }

  .detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

.detail-section {
  margin-bottom: 20px;

  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: #81C784;
    margin-bottom: 12px;
    padding-left: 10px;
    border-left: 3px solid #4CAF50;
  }
}

.customer-info {
  .info-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .info-item {
      padding: 10px 12px;
      background: rgba(76, 175, 80, 0.04);
      border: 1px solid rgba(76, 175, 80, 0.08);
      border-radius: 8px;

      .info-label {
        display: block;
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.8;
        margin-bottom: 4px;
      }

      .info-value {
        font-size: 14px;
        font-weight: 600;
        color: #e8f5e9;

        &.vip {
          color: #FFB74D;
        }

        &.gold {
          color: #81C784;
        }

        &.silver {
          color: #a5d6a7;
        }

        &.normal {
          color: #66BB6A;
        }
      }
    }
  }
}

.ticket-detail-content {
  .detail-desc {
    font-size: 13px;
    color: #a5d6a7;
    line-height: 1.8;
    padding: 12px;
    background: rgba(76, 175, 80, 0.04);
    border-radius: 8px;
    border: 1px solid rgba(76, 175, 80, 0.08);
  }

  .detail-attachments {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .attachment-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      background: rgba(76, 175, 80, 0.04);
      border: 1px solid rgba(76, 175, 80, 0.08);
      border-radius: 6px;

      .att-icon {
        font-size: 14px;
      }

      .att-name {
        flex: 1;
        font-size: 12px;
        color: #a5d6a7;
      }

      .att-size {
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.7;
      }
    }
  }
}

// ===== 时间线 =====
.timeline {
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(76, 175, 80, 0.15);
  }
}

.timeline-item {
  position: relative;
  padding-bottom: 16px;

  &:last-child {
    padding-bottom: 0;
  }

  .timeline-dot {
    position: absolute;
    left: -17px;
    top: 4px;
    width: 9px;
    height: 9px;
    background: #4CAF50;
    border: 2px solid #0d1f0f;
    border-radius: 50%;

    &.latest {
      background: #81C784;
      box-shadow: 0 0 8px rgba(129, 199, 132, 0.5);
    }

    &.system {
      background: #66BB6A;
    }
  }

  .timeline-content {
    .timeline-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 4px;
      flex-wrap: wrap;

      .timeline-action {
        font-size: 13px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .timeline-author {
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.8;
      }

      .timeline-time {
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.6;
        margin-left: auto;
      }
    }

    .timeline-desc {
      font-size: 12px;
      color: #a5d6a7;
      line-height: 1.6;
      padding: 8px 12px;
      background: rgba(76, 175, 80, 0.04);
      border-radius: 6px;
      border: 1px solid rgba(76, 175, 80, 0.06);
    }
  }
}

// ===== 详情操作按钮 =====
.detail-actions {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid rgba(76, 175, 80, 0.08);

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}

// ===== 弹窗 =====
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  background: linear-gradient(135deg, #102A15 0%, #0d2311 100%);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #e8f5e9;
  }

  .modal-close {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background: rgba(239, 83, 80, 0.15);
      border-color: rgba(239, 83, 80, 0.3);
      color: #EF5350;
    }
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.full {
      grid-column: 1 / -1;
    }

    label {
      font-size: 12px;
      color: #81C784;

      .required {
        color: #EF5350;
      }
    }

    input,
    select,
    textarea {
      padding: 8px 12px;
      background: rgba(16, 42, 21, 0.8);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #e8f5e9;
      font-size: 13px;
      outline: none;

      &::placeholder {
        color: #4CAF50;
        opacity: 0.4;
      }

      &:focus {
        border-color: #4CAF50;
      }
    }

    textarea {
      resize: vertical;
      min-height: 80px;
      font-family: inherit;
    }

    option {
      background: #102A15;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(76, 175, 80, 0.1);

  button {
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;

    &.btn-secondary {
      background: rgba(255, 255, 255, 0.05);
      color: #a5d6a7;
      border: 1px solid rgba(76, 175, 80, 0.2);

      &:hover {
        background: rgba(76, 175, 80, 0.1);
      }
    }

    &.btn-primary {
      background: linear-gradient(135deg, #2E7D32, #1B5E20);
      color: #e8f5e9;

      &:hover {
        background: linear-gradient(135deg, #388E3C, #2E7D32);
      }
    }
  }
}

// ===== 响应式 =====
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
  }

  .ticket-section.detail-open {
    flex: 1 !important;
    max-height: 400px;
  }

  .detail-panel {
    min-height: 400px;
  }
}
</style>